const {ESLMediaQuery} = require('@exadel/esl/modules/esl-media-query/core');
const {AmpersandView} = require('catalog/libs');

const HPEServices = require('../../base/hpe.services');
const HPEConstants = require('../../base/hpe.constants');
const stickytop = require('../../base/hpe.sticky.top');

/**
 * Dyanmic Tab Container view
 */
var DynamicTabContainerView = AmpersandView.extend({

    autoRender: false,

    /**
     * view initialization
     */
    initialize: function() {

        //cached selectors
        this.containerElement = this.query('.dynamic-tab-container-content');
        this.navElement = this.query('.dynamic-tab-container-nav');
        this.tabElementList = this.queryAll('[data-hook=dynamic-tab-container-tabs] a');
        this.panelElement = this.query('[data-hook=dynamic-tab-container-panel]');
        this.mobileCloseElement = this.query('[data-hook=dynamic-tab-container-panel-close]');

        //non-mobile view initialization
        if (this.tabElementList.length > 1) {

            //helper/utility initialization
            stickytop.initialize([{
                stickyElement: this.navElement,
                measureElement: this.containerElement,
                offsetValue: this.navElement.clientHeight,
                className: 'dynamic-tab-container-nav-fixed'
            }]);

        //if less than 2 tabs, non-mobile view should not display tabs
        } else {
            document.querySelector('.dynamic-tab-container').classList.add('no-tabs');
        }

        //always keep track of breakpoint since mobile/non-mobile views are so different
        this.isMobileCheck();
        ESLMediaQuery.for('@XS').addEventListener(this.isMobileCheck.bind(this));

        //capture first initialization of child component on page load
        this.listenToOnce(this.model, 'change:currentDynamicTabConstant', this.onInitialActiveTabInitialized);

        //manage loading indicator for child elements
        HPEServices.subscribe(HPEConstants.dynamictabcontainer.LOADER_SHOW, this, this.onLoaderShow);
        HPEServices.subscribe(HPEConstants.dynamictabcontainer.LOADER_HIDE, this, this.onLoaderHide);
    },

    /**
     * bindings
     */
    bindings: {
        'model.isLoading': {
            type: 'booleanClass',
            hook: 'dynamic-tab-container-panel',
            name: 'loading'
        },
        'model.tabClass': {
            type: function (el, value) {
                el.setAttribute('data-tab-class', value);
            },
            hook: 'dynamic-tab-container-panel-close'
        }
    },

    /**
     * override render function to not render anything
     */
    render: function() {
        return this;
    },

    /**
     * events
     */
    events: {
        'click [data-hook=dynamic-tab-container-tabs] a': 'onTabSelect',
        'click [data-hook=dynamic-tab-container-panel-close] a': 'onMobileViewClose'
    },

    /**
     * reset the tab content pane view to be ready for new content
     */
    resetContentPaneView: function() {

        var contentSelector = 'dynamic-tab-container-panel-content';
        var contentElement = document.createElement('div');

        //remove all children
        while (this.panelElement.firstChild) {
            this.panelElement.removeChild(this.panelElement.firstChild);
        }

        //recreate container for tab panel content (is removed by each render)
        contentElement.setAttribute('id', contentSelector);
        contentElement.setAttribute('data-hook', contentSelector);
        this.panelElement.appendChild(contentElement);

        this.onLoaderHide();
    },

    /**
     * update the UI to match views corresponding to passed id
     * @param {string} id indicator of requested view
     */
    update: function(id) {

        //update the existing view content pane to be ready for requested view
        this.resetContentPaneView();

        //update the UI of the tabs and panel
        this.updateTabUI(id);
        this.updatePanelUI(true);

        //broadcast change event
        HPEServices.publish(HPEConstants.dynamictabcontainer.CHANGE, id);
    },

    /**
     * update "active" UI of the tab bar with the passed id
     * @param {string} id indicator of requested tab
     */
    updateTabUI: function(id) {

        var domID;

        //do not proceed without id of selected tab
        //or if selected id is already selected
        if (id && (id !== this.model.activeTabID)) {

            //update model with elements from selected tab
            this.model.activeTabID = id;

            //update DOM to reflect tab change
            this.tabElementList.forEach(function(tab) {

                var tabID = tab.getAttribute('data-instance-class');

                if (tabID === id) {
                    tab.classList.add('active');
                    tab.setAttribute('aria-selected', 'true');
                    domID = tab.getAttribute('id');
                } else {
                    tab.classList.remove('active');
                    tab.setAttribute('aria-selected', 'false');
                }
            });

            //aria update link between tabs + panel
            this.panelElement.setAttribute('aria-labelledby', domID);
        }
    },

    /**
     * update "active" UI of the panel
     * @param {boolean} isOpen optional boolean true when view is visible (for mobile)
     */
    updatePanelUI: function(isOpen) {

        //mobile view requires additional CSS classes & open/close/scroll functionality
        if (this.model.isMobile) {

            //open the mobile modal
            if (isOpen) {
                document.body.classList.add('modal-open');
                this.panelElement.scrollTop = 0;
                this.panelElement.classList.add('open');
                this.mobileCloseElement.classList.add('open');
                HPEServices.publish(HPEConstants.dynamictabcontainer.MOBILE_OPEN_CLOSE, {
                    id: this.model.activeTabID,
                    isOpen: true
                });

            //close the mobile modal
            } else {
                document.body.classList.remove('modal-open');
                this.panelElement.classList.remove('open');
                this.mobileCloseElement.classList.remove('open');
                HPEServices.publish(HPEConstants.dynamictabcontainer.MOBILE_OPEN_CLOSE, {
                    id: this.model.activeTabID,
                    isOpen: false
                });
            }

        //non-mobile view
        } else {

            //jump page content view to top of tab pane area on tab select
            //but only if nav is in fixed position
            if (this.navElement.classList.contains('dynamic-tab-container-nav-fixed')) {
                this.containerElement.scrollIntoView();
            }
        }
    },

    /**
     * handle tab selection event
     * @param {object} e event object
     */
    onTabSelect: function(e) {

        var id = e.target.getAttribute('data-instance-class');
        var page = e.target.getAttribute('data-page');
        var tabClass = e.target.getAttribute('data-tab-class');

        e.stopPropagation();
        e.preventDefault();
        e.stopPropagation();

        //broadcast event passing component class constant for changed tab unique ID
        if (id && page) {

            //mobile users can reselect the same tab/panel multiple times in a row, but non-mobile users cannot
            if (this.model.isMobile || (!this.model.activeTabID) || ((!this.model.isMobile) && (id !== this.model.activeTabID))) {

                //save current tab instance "tab class" for UI customization on tab
                this.model.tabClass = tabClass;

                //push router change event to trigger view/tab change
                HPEServices.publish(HPEConstants.router.ROUTER_UPDATE_PAGE, {
                    page: page,
                    state: {
                        id: id
                    }
                });

                //update canonical url and meta og:url
                document.querySelector('[property="og:url"]').content = window.location.href;
                document.querySelector('[rel="canonical"]').href = window.location.href;
            }
        }
    },

    /**
     * handle initial setting of currentDynamicTabConstant value in model
     * represents initial tab state on page load
     * only called once
     */
    onInitialActiveTabInitialized: function(e) {

        //if initialized on page load in mobile view on a tab other than the first tab,
        //that tab panel should automatically open
        if (this.model.isMobile) {
            if (e.currentDynamicTabConstant !== this.model.validTabConstants[0]) {
                this.updatePanelUI(true);
            }
        }
    },

    /**
     * handle click close of mobile panel view
     * @param {object} e event object
     */
    onMobileViewClose: function(e) {

        e.preventDefault();
        this.updatePanelUI(false);
    },

    /**
     * handle loader show event
     */
    onLoaderShow: function() {
        this.model.isLoading = true;
    },

    /**
     * handle loader hide event
     */
    onLoaderHide: function() {
        this.model.isLoading = false;
    },

    /**
     * update isMobile flag based on current breakpoint
     */
    isMobileCheck: function() {
        this.model.isMobile = ESLMediaQuery.for('@XS').matches;
    }
});

module.exports = DynamicTabContainerView;
